// pages/TranslationEditor.jsx - Complete implementation with full-screen viewer
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// Services
import translationService from '../services/translationService';
import documentService from '../services/documentService';
import useApiClient from '../services/api';

// Components
import Spinner from '../components/Spinner';
import FullScreenTranslationViewer from '../components/FullScreenTranslationViewer';
import FullScreenGoogleDocsViewer from '../components/FullScreenGoogleDocsViewer';
import GoogleDocsEditor from '../components/GoogleDocsEditor';
import SignatureStampView from '../components/SignatureStampView';

// Hooks
import { useChatbot } from '../hooks/useChatbot';
import { useAuth } from '../context/AuthContext';
import useUserService from '../hooks/useUserService';

// Service
import GoogleDocsService from '../services/googleDocsService';

const TranslationEditor = () => {
  const { t } = useTranslation();
  const { translationId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editorRef = useRef(null);
  const apiClient = useApiClient();

  // Always use Google Docs mode
  const useGoogleDocs = true;

  const { isGoogleAuthenticated, googleAccessToken, googleLogin, isGoogleLoading } = useAuth();
  const { userProfile } = useUserService();

  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [selectionContext, setSelectionContext] = useState('');

  // Google Docs specific state
  const [googleDocId, setGoogleDocId] = useState(null);
  const [translatedDocxBlob, setTranslatedDocxBlob] = useState(null);
  const [translation, setTranslation] = useState({
    id: '',
    projectId: '',
    projectName: '',
    documentId: '',
    documentTitle: '',
    originalFileName: '',
    originalDocument: null,
    aiTranslation: '',
    aiTranslationUrl: null,
    finalTranslation: '',
    sourceLanguage: '',
    targetLanguage: '',
    additionalContext: '',
    documentType: '',
    documentHolder: '',
    status: '',
    readyForApproval: false,
    latestFinalDocument: null,
    finalDocuments: [],
    retranslationComments: []
  });
  const [htmlContent, setHtmlContent] = useState('');
  const [htmlFiles, setHtmlFiles] = useState([]);
  const [showSignatureView, setShowSignatureView] = useState(false);

  // Initialize selectedPage from URL query param or default to 1
  const [selectedPage, setSelectedPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });

  const htmlFilesRef = useRef([]); // Track latest htmlFiles for auto-save

  // Keep ref in sync with state
  useEffect(() => {
    htmlFilesRef.current = htmlFiles;
  }, [htmlFiles]);

  // Update URL when page changes
  useEffect(() => {
    setSearchParams({ page: selectedPage.toString() }, { replace: true });
  }, [selectedPage, setSearchParams]);

  // Initialize chatbot with dynamic configuration
  const { config: chatbotConfig, updateConfig: updateChatbotConfig } = useChatbot({
    enabled: true,
    position: 'bottom-right',
    title: 'Translation Assistant',
    welcomeMessage: `Hi! I can help you improve this translation. You can:
    
🔧 **Quick Commands:**
• Type "/format" to improve formatting
• Type "/grammar" to fix grammar issues
• Type "/formal" to make text more formal
• Type "/casual" to make text more casual
• Type "/translate [text]" to retranslate specific text

📝 **Text Selection:**
• Select text in the editor, then ask me to improve it
• I'll focus on your selected text and provide context-aware suggestions

What would you like me to help you with?`,
    apiEndpoint: '/documents'
  });

  // Update chatbot configuration based on translation context
  useEffect(() => {
    if (translation.sourceLanguage && translation.targetLanguage) {
      updateChatbotConfig({
        title: `${translation.sourceLanguage.toUpperCase()} → ${translation.targetLanguage.toUpperCase()} Assistant`,
        welcomeMessage: `Hi! I'm helping you translate from ${translation.sourceLanguage.toUpperCase()} to ${translation.targetLanguage.toUpperCase()}.

🔧 **Quick Commands:**
• "/format" - Improve formatting
• "/grammar" - Fix grammar issues  
• "/formal" - Make text more formal
• "/casual" - Make text more casual
• "/translate [text]" - Retranslate specific text
• "/context" - Add more context to translation

📝 **Text Selection:**
Select any text in the editor and I'll help improve just that part!

What would you like me to help you with?`
      });
    }
  }, [translation.sourceLanguage, translation.targetLanguage, updateChatbotConfig]);

  // Handle text selection in TinyMCE editor
  const handleSelectionChange = (editor) => {
    if (editor) {
      const selection = editor.selection;
      
      if (selection) {
        const selectedContent = selection.getContent({ format: 'text' });
        
        if (selectedContent && selectedContent.trim().length > 0) {
          setSelectedText(selectedContent.trim());
          
          // Get surrounding context (previous and next 100 characters)
          const fullContent = editor.getContent({ format: 'text' });
          const selectionStart = fullContent.indexOf(selectedContent);
          const contextStart = Math.max(0, selectionStart - 100);
          const contextEnd = Math.min(fullContent.length, selectionStart + selectedContent.length + 100);
          const context = fullContent.substring(contextStart, contextEnd);
          
          setSelectionContext(context);
          
          // Update chatbot with selection info
          updateChatbotConfig({
            welcomeMessage: `I see you've selected: "${selectedContent.substring(0, 100)}${selectedContent.length > 100 ? '...' : ''}"

I can help you:
• Improve this specific text
• Retranslate it with different style
• Fix grammar or formatting issues
• Make it more formal/casual

What would you like me to do with this selection?`
          });
        } else {
          setSelectedText('');
          setSelectionContext('');
        }
      }
    }
  };

  // Fetch translation data
  useEffect(() => {
    // Don't start fetching until Google auth has initialized
    if (isGoogleLoading) {
      console.log('⏳ Waiting for Google auth to initialize...');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        if (translationId) {
          // Always try document flow first (new flow with Google Docs)
          try {
            // Check the translation status
            const statusData = await documentService(apiClient).checkTranslationStatus(translationId);
            const titleMeta = await fetchTranslationMeta(translationId);
            const resolvedTitleMeta = {
              ...titleMeta,
              documentTitle: titleMeta.documentTitle || statusData.documentTitle
            };

            // If status is completed or has results available, proceed
            if (statusData.status === 'completed' || (statusData.dropbox && statusData.dropbox.hasResults)) {
              // Always use Google Docs mode
              if (useGoogleDocs) {
                // Fetch DOCX and import to Google Docs
                await fetchAndImportDocx(translationId, resolvedTitleMeta);
              } else {
                // Fallback to HTML content (should not happen)
                await fetchTranslatedHtmls(translationId);
              }

              console.log('Fetched translated content for document ID:', statusData);
              // Set basic translation data
              setTranslation(prev => ({
                ...prev,
                documentType: 'Document',
                documentHolder: resolvedTitleMeta.projectName || translationId,
                status: statusData.status,
                id: resolvedTitleMeta.id || prev.id,
                projectId: resolvedTitleMeta.projectId || prev.projectId,
                projectName: resolvedTitleMeta.projectName || prev.projectName,
                documentId: resolvedTitleMeta.documentId || translationId,
                documentTitle: resolvedTitleMeta.documentTitle || statusData.documentTitle,
                originalFileName: resolvedTitleMeta.originalFileName || resolvedTitleMeta.documentTitle || prev.originalFileName,
                sourceLanguage: resolvedTitleMeta.sourceLanguage || prev.sourceLanguage,
                targetLanguage: resolvedTitleMeta.targetLanguage || prev.targetLanguage,
                additionalContext: resolvedTitleMeta.additionalContext || prev.additionalContext,
                readyForApproval: resolvedTitleMeta.readyForApproval || statusData.readyForApproval || false,
                latestFinalDocument: resolvedTitleMeta.latestFinalDocument || prev.latestFinalDocument,
                finalDocuments: resolvedTitleMeta.finalDocuments || prev.finalDocuments || [],
                retranslationComments: resolvedTitleMeta.retranslationComments || []
              }));

              // If still processing, start polling for completion
              if (statusData.status === 'processing') {
                startStatusPolling(translationId);
              }
            } else {
              toast.warning(t('dashboard.messages.loadDataFailed'));
              navigate('/translator/dashboard');
              return;
            }
          } catch (error) {
            console.log('New document flow failed, trying traditional flow:', error);

            // Fallback to traditional flow (for old translations)
            try {
              const fallbackMeta = await fetchTranslationMeta(translationId);
              const legacyTranslationId = fallbackMeta.id || translationId;
              const data = await translationService(apiClient).getTranslationById(legacyTranslationId);
              setTranslation(data);

              // If the AI translation is a URL, we need to fetch the content separately
              if (data.aiTranslationUrl && !data.aiTranslation) {
                await fetchAiTranslationContent(data.aiTranslationUrl);
              }
            } catch (innerError) {
              console.error('Error fetching translation data:', innerError);
              toast.error(t('dashboard.messages.loadDataFailed'));
              navigate('/translator/dashboard');
            }
          }
        } else {
          toast.error(t('dashboard.messages.loadDataFailed'));
          navigate('/translator/dashboard');
          return;
        }
      } catch (error) {
        console.error('Error fetching translation:', error);
        toast.error(t('dashboard.messages.loadDataFailed'));
        navigate('/translator/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();

  }, [translationId, isGoogleLoading, isGoogleAuthenticated]);

  // Function to start polling for translation status updates
  const startStatusPolling = (documentId) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusData = await documentService(apiClient).checkTranslationStatus(documentId);
        console.log('Polling translation status:', statusData);
        
        if (statusData.status === 'completed') {
          clearInterval(pollInterval);
          // Update translation status
          setTranslation(prev => ({
            ...prev,
            status: 'completed'
          }));
          
          // Refresh HTML content to get final version
          await fetchTranslatedHtmls(documentId);

          toast.success(t('googleDocs.messages.translateSuccess'));
        }
      } catch (error) {
        console.error('Error polling status:', error);
        clearInterval(pollInterval);
      }
    }, 5000); // Poll every 5 seconds
    
    // Clean up polling after 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
    }, 600000);
  };

  // Fetch AI translation content from URL
  const fetchAiTranslationContent = async (url) => {
    console.log(contentLoading)
    try {
      setContentLoading(true);
      console.log('Fetching translation content from:', url);
      
      // Fetch the content from the URL
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch translation content: ${response.statusText}`);
      }
      
      // Try to determine the content type
      const contentType = response.headers.get('content-type');
      console.log('Content type:', contentType);
      
      let content = '';
      if (contentType && contentType.includes('application/json')) {
        // Handle JSON response
        const jsonData = await response.json();
        content = jsonData.content || JSON.stringify(jsonData, null, 2);
      } else if (contentType && contentType.includes('text/html')) {
        // For HTML files, keep the HTML structure for TinyMCE
        content = await response.text();
      } else if (contentType && (contentType.includes('text/plain'))) {
        // Handle text response - convert newlines to <br> tags for better rendering
        const textContent = await response.text();
        // Preserve paragraphs and line breaks by converting to HTML
        content = textContent
          .split('\n\n')
          .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
          .join('');
      } else if (contentType && contentType.includes('application/pdf')) {
        // For PDF files, we can't directly load the content into TinyMCE
        content = "<p>This is a PDF file. The AI translation content cannot be displayed directly in the editor. Please refer to the original document panel.</p>";
      } else {
        // Default case - try to get as text and convert to HTML
        const textContent = await response.text();
        content = `<p>${textContent.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
      }
      
      console.log('Processed content:', content.substring(0, 100) + '...');
      
      // Update translation state with the fetched content
      setTranslation(prev => ({
        ...prev,
        aiTranslation: content
      }));
      
      // Also set the HTML content
      setHtmlContent(content);
      console.log('AI translation content loaded successfully', content);
    } catch (error) {
      console.error('Error fetching AI translation content:', error);
      toast.error(t('dashboard.messages.loadDataFailed'));
    } finally {
      setContentLoading(false);
    }
  };

  const fetchTranslationMeta = async (documentId) => {
    try {
      const response = await apiClient.get(`/translations/${documentId}/status`);
      if (response.data?.success && response.data.translation) {
        const translationMeta = response.data.translation;
        return {
          id: translationMeta.id,
          projectName: translationMeta.project?.name,
          projectId: translationMeta.project?.id || null,
          documentTitle: translationMeta.originalFileName,
          originalFileName: translationMeta.originalFileName,
          documentId: translationMeta.documentId,
          sourceLanguage: translationMeta.sourceLanguage,
          targetLanguage: translationMeta.targetLanguage,
          additionalContext: translationMeta.additionalContext,
          readyForApproval: translationMeta.readyForApproval || translationMeta.ready_for_approval || false,
          latestFinalDocument: translationMeta.latestFinalDocument || null,
          finalDocuments: translationMeta.finalDocuments || [],
          retranslationComments: Array.isArray(translationMeta.retranslationComments)
            ? translationMeta.retranslationComments
            : []
        };
      }
    } catch (error) {
      console.warn('[TranslationEditor] Failed to fetch translation metadata:', error);
    }

    return {};
  };

  // Fetch and import DOCX to Google Docs
  const fetchAndImportDocx = async (documentId, titleMeta = {}) => {
    try {
      setContentLoading(true);

      // Check Google authentication
      if (!isGoogleAuthenticated) {
        console.error('❌ Not authenticated with Google');
        toast.error(t('googleDocs.messages.loginRequired'));
        googleLogin();
        return;
      }

      console.log('✅ Google authenticated, fetching document info from backend...');
      console.log(`📡 Calling: GET /documents/${documentId}/docx`);

      // Fetch DOCX or existing Google Doc ID from backend
      const response = await documentService(apiClient).getTranslatedDocx(documentId);

      console.log('📦 Backend response:', response);

      if (response.success) {
        // 🆕 Check if Google Doc already exists
        if (response.useExisting && response.googleDocId) {
          console.log(`✅ Reusing existing Google Doc: ${response.googleDocId}`);
          setGoogleDocId(response.googleDocId);
          toast.success(t('googleDocs.messages.loadSuccess'));

          // Set the preview URL for original document
          const previewUrl = documentService(apiClient).getOriginalDocumentPreviewUrl(documentId);
          setTranslation(prev => ({
            ...prev,
            originalDocument: previewUrl
          }));

          return; // ✅ Skip DOCX upload since we're reusing existing Google Doc
        }

        // 🆕 If no existing Google Doc, create new one from DOCX
        console.log('⚠️ No existing Google Doc found - creating new one from DOCX...');
        console.log('Base64 length:', response.docx?.length || 0);

        // Validate base64 data
        if (!response.docx || response.docx.length === 0) {
          throw new Error('Backend returned empty DOCX data');
        }

        // Convert base64 to Blob
        const base64Data = response.docx;

        try {
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          });

          console.log(`✅ Blob created (${blob.size} bytes)`);

          // Validate blob size
          if (blob.size === 0) {
            throw new Error('Created blob has zero size');
          }

          // Check if it looks like a valid DOCX (should start with PK for ZIP format)
          const firstBytes = new Uint8Array(bytes.slice(0, 2));
          if (firstBytes[0] === 0x50 && firstBytes[1] === 0x4B) {
            console.log('✅ File starts with PK signature (valid ZIP/DOCX format)');
          } else {
            console.warn('⚠️ File does not start with PK signature - may not be valid DOCX');
            console.warn('First bytes:', firstBytes);
          }

          setTranslatedDocxBlob(blob);

          // Convert Blob to File
          const file = new File([blob], response.filename || `translated-${documentId}.docx`, {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          });

          console.log('📤 Importing to Google Docs...');

          // Import DOCX to Google Docs
          const googleDocs = new GoogleDocsService(googleAccessToken);
          const projectName = titleMeta.projectName || translation.documentHolder || translationId || documentId;
          const documentName = titleMeta.documentTitle || translation.documentTitle || documentId;
          const sourceLanguage = titleMeta.sourceLanguage || translation.sourceLanguage || 'source';
          const targetLanguage = titleMeta.targetLanguage || translation.targetLanguage || 'target';
          const googleDocTitle = `Translation_${projectName}_${documentName}_${sourceLanguage}_${targetLanguage}`;
          const { documentId: googleDocId } = await googleDocs.importDocx(
            file,
            googleDocTitle
          );

          console.log(`✅ Imported to Google Docs! Doc ID: ${googleDocId}`);
          setGoogleDocId(googleDocId);

          // 🆕 Save Google Doc ID to backend
          try {
            await documentService(apiClient).saveGoogleDocId(documentId, googleDocId);
            console.log(`✅ Saved Google Doc ID to backend: ${googleDocId}`);
          } catch (saveError) {
            console.warn('⚠️ Failed to save Google Doc ID to backend:', saveError);
            // Don't throw - document is already loaded, just log the warning
          }

          toast.success(t('googleDocs.messages.uploadSuccess'));

          // Set the preview URL for original document
          const previewUrl = documentService(apiClient).getOriginalDocumentPreviewUrl(documentId);
          setTranslation(prev => ({
            ...prev,
            originalDocument: previewUrl
          }));
        } catch (conversionError) {
          console.error('❌ Error converting base64 to blob:', conversionError);
          throw new Error(`Failed to convert DOCX data: ${conversionError.message}`);
        }
      } else {
        console.error('❌ Backend returned success: false');
        throw new Error('Backend returned success: false');
      }
    } catch (error) {
      console.error('❌ Error in fetchAndImportDocx:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      toast.error(t('googleDocs.messages.uploadFailed', { error: error.message }));
      throw error; // Re-throw so the parent catches it
    } finally {
      setContentLoading(false);
    }
  };

  // Fetch translated HTML files for a document
  const fetchTranslatedHtmls = async (documentId) => {
    try {
      setContentLoading(true);

      const response = await documentService(apiClient).getTranslatedHTMLs(documentId);

      if (response.success) {
        // Set the preview URL for original document
        const previewUrl = documentService(apiClient).getOriginalDocumentPreviewUrl(documentId);
        setTranslation(prev => ({
          ...prev,
          originalDocument: previewUrl
        }));

        // Set HTML files
        if (response.htmlFiles && response.htmlFiles.length > 0) {
          setHtmlFiles(response.htmlFiles);

          // Get page from URL or default to 1
          const pageParam = searchParams.get('page');
          const pageToLoad = pageParam ? parseInt(pageParam, 10) : 1;

          // Ensure page is within valid range
          const validPage = Math.min(Math.max(pageToLoad, 1), response.htmlFiles.length);

          if (response.htmlFiles[validPage - 1]) {
            setSelectedPage(validPage);
            setHtmlContent(response.htmlFiles[validPage - 1].content);
            // Set lastSaved timestamp if available
            if (response.htmlFiles[validPage - 1].lastSaved) {
              setLastSaved(response.htmlFiles[validPage - 1].lastSaved);
            }
          }
        } else {
          toast.warning(t('dashboard.messages.loadDataFailed'));
        }
      } else {
        toast.warning(t('dashboard.messages.loadDataFailed'));
      }
    } catch (error) {
      console.error('Error fetching translated HTMLs:', error);
      toast.error(t('dashboard.messages.loadDataFailed'));
    } finally {
      setContentLoading(false);
    }
  };

  // Handle page selection change with content synchronization
  const handlePageChange = (pageNumber) => {
    // Save current content from editor to ref and state before switching
    if (editorRef.current && htmlFilesRef.current.length > 0) {
      const currentPageIndex = selectedPage - 1;
      const currentContent = editorRef.current.getContent();

      // Always capture the latest content from editor when switching pages
      const updatedHtmlFiles = [...htmlFilesRef.current];
      updatedHtmlFiles[currentPageIndex] = {
        ...updatedHtmlFiles[currentPageIndex],
        content: currentContent
      };

      // Update both ref and state for page changes (re-render is expected)
      htmlFilesRef.current = updatedHtmlFiles;
      setHtmlFiles(updatedHtmlFiles);
    }

    // Switch to the new page using the ref (most up-to-date data)
    const pageIndex = pageNumber - 1;
    if (htmlFilesRef.current[pageIndex]) {
      setSelectedPage(pageNumber);
      setHtmlContent(htmlFilesRef.current[pageIndex].content);
    }
  };

  useEffect(() => {
  console.log('htmlContent updated:', htmlContent.substring(0, 100));
}, [htmlContent]);



  // Enhanced HTML update handler with selection support
  const handleHtmlUpdate = async (newHtml) => {
    console.log('Auto-saving HTML content for page', selectedPage);

    // Set saving state to true
    setSaving(true);

    // Update the current page in htmlFiles REF ONLY (no state update to prevent re-render)
    let updatedHtmlFiles;
    if (htmlFilesRef.current.length > 0) {
      const currentPageIndex = selectedPage - 1;
      if (htmlFilesRef.current[currentPageIndex]) {
        updatedHtmlFiles = [...htmlFilesRef.current];
        updatedHtmlFiles[currentPageIndex] = {
          ...updatedHtmlFiles[currentPageIndex],
          content: newHtml
        };
        htmlFilesRef.current = updatedHtmlFiles; // Update ref immediately
        // DO NOT call setHtmlFiles or setHtmlContent here - it causes re-render and resets cursor!
      }
    }
    // Single page mode - still need to track content in ref for consistency
    // DO NOT update state during auto-save

    // Auto-save to backend (silently, no toast notifications)
    try {
      if (!translation.documentId) {
        // Traditional flow
        await translationService(apiClient).saveTranslation(translationId, newHtml);
      } else {
        // New document flow - save all pages with latest content
        const filesToSave = updatedHtmlFiles || htmlFilesRef.current;
        if (filesToSave.length > 0) {
          await translationService(apiClient).saveTranslation(translationId, filesToSave);
          console.log('✅ Auto-saved page', selectedPage, 'to backend');
        }
      }
      // Update lastSaved timestamp on successful save
      setLastSaved(new Date().toISOString());
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Don't show error to user for auto-save failures
    } finally {
      // Set saving state to false after save completes
      setSaving(false);
    }

    // Clear selection after update
    setSelectedText('');
    setSelectionContext('');

    // Reset chatbot welcome message
    updateChatbotConfig({
      welcomeMessage: `Great! I've updated the content.

🔧 **Quick Commands:**
• "/format" - Improve formatting
• "/grammar" - Fix grammar issues
• "/formal" - Make text more formal
• "/casual" - Make text more casual
• "/translate [text]" - Retranslate specific text

📝 Select any text to get specific help with that section!`
    });
  };

  // Enhanced chatbot message handler with command processing
  const handleChatbotMessage = async (message, selectedText, context) => {
    // Process quick commands
    if (message.startsWith('/')) {
      const command = message.toLowerCase();
      let processedMessage = '';
      
      if (command === '/format') {
        processedMessage = selectedText 
          ? `Improve the formatting and structure of this selected text: "${selectedText}"`
          : 'Improve the overall formatting and structure of the document';
      } else if (command === '/grammar') {
        processedMessage = selectedText
          ? `Fix any grammar issues in this selected text: "${selectedText}"`
          : 'Check and fix any grammar issues in the document';
      } else if (command === '/formal') {
        processedMessage = selectedText
          ? `Make this selected text more formal: "${selectedText}"`
          : 'Make the entire document more formal in tone';
      } else if (command === '/casual') {
        processedMessage = selectedText
          ? `Make this selected text more casual: "${selectedText}"`
          : 'Make the entire document more casual in tone';
      } else if (command.startsWith('/translate ')) {
        const textToTranslate = command.substring(11);
        processedMessage = `Retranslate this text with improved accuracy: "${textToTranslate}"`;
      } else if (command === '/context') {
        processedMessage = selectedText
          ? `Provide better contextual translation for: "${selectedText}". Consider the surrounding context: "${context}"`
          : 'Improve the overall translation considering document context';
      } else {
        processedMessage = message; // Use original message if command not recognized
      }
      
      return processedMessage;
    }
    
    // If there's selected text, include it in the message
    if (selectedText) {
      return `${message}

**Selected text:** "${selectedText}"
**Context:** "${context}"

Please focus on improving just the selected text based on my request.`;
    }
    
    return message;
  };

  // Save translation
  const handleSaveTranslation = async () => {
    if (editorRef.current) {
      try {
        setSaving(true);
        // Get current content directly from editor
        const content = editorRef.current.getContent();
        console.log('Manual save: Saving content:', content.substring(0, 100) + '...');

        // For traditional flow
        if (!translation.documentId) {
          await translationService(apiClient).saveTranslation(translationId, content);
        } else {
          // For new document flow, save the current page with latest content from editor
          const currentPageIndex = selectedPage - 1;

          if (htmlFilesRef.current[currentPageIndex]) {
            // Update the content in the ref first
            const updatedHtmlFiles = [...htmlFilesRef.current];
            updatedHtmlFiles[currentPageIndex] = {
              ...updatedHtmlFiles[currentPageIndex],
              content: content
            };

            // Update both ref and state (state update is OK for manual saves)
            htmlFilesRef.current = updatedHtmlFiles;
            setHtmlFiles(updatedHtmlFiles);

            await translationService(apiClient).saveTranslation(translationId, updatedHtmlFiles);
          }
        }

        // Update the local state
        setTranslation(prev => ({
          ...prev,
          finalTranslation: content
        }));

        // Update lastSaved timestamp on successful save
        setLastSaved(new Date().toISOString());

        toast.success(t('common.success'));
      } catch (error) {
        console.error('Error saving translation:', error);
        toast.error(t('dashboard.messages.loadDataFailed'));
      } finally {
        setSaving(false);
      }
    }
  };

  // Complete and approve translation
  const handleApproveTranslation = async () => {
    if (editorRef.current) {
      try {
        setSaving(true);
        const content = editorRef.current.getContent();

        // For traditional flow
        if (!translation.documentId) {
          await translationService(apiClient).approveTranslation(translationId, content);
        } else {
          // For new document flow, approve all pages
          // TODO: Add endpoint to approve HTML content
          toast.info(t('common.success'));
        }

        toast.success(t('common.success'));
        navigate('/translator/dashboard');
      } catch (error) {
        console.error('Error approving translation:', error);
        toast.error(t('dashboard.messages.loadDataFailed'));
      } finally {
        setSaving(false);
      }
    }
  };

  // Export Google Doc to DOCX
  const handleExportGoogleDoc = async () => {
    if (!googleDocId || !googleAccessToken) {
      toast.error(t('googleDocs.messages.noDocToExport'));
      return;
    }

    try {
      setSaving(true);
      toast.info(t('googleDocs.messages.exporting'));

      const googleDocs = new GoogleDocsService(googleAccessToken);
      const blob = await googleDocs.exportToDocx(googleDocId);
      let liveTitle = null;
      try {
        const metadata = await googleDocs.getFileMetadata(googleDocId);
        liveTitle = metadata?.name || null;
      } catch (metadataError) {
        console.warn('Could not fetch live Google Doc title for DOCX export:', metadataError.message);
      }
      const fallbackTitle = translation?.documentTitle || translationId || 'translation';
      const baseName = (liveTitle || fallbackTitle).replace(/\.[^/.]+$/, '').trim();
      const safeBaseName = baseName.replace(/[^a-z0-9_-]/gi, '_').replace(/_+/g, '_') || 'translation';

      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeBaseName}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('googleDocs.messages.exportSuccess'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('googleDocs.messages.exportFailed', { error: error.message }));
    } finally {
      setSaving(false);
    }
  };

  const buildCurrentSigningTranslation = () => ({
    id: translation.id,
    documentId: translation.documentId || translationId,
    originalFileName: translation.originalFileName || translation.documentTitle || `Translation ${translationId}`,
    sourceLanguage: translation.sourceLanguage,
    targetLanguage: translation.targetLanguage,
    projectId: translation.projectId,
    status: 'COMPLETED',
    googleDocId,
    dropboxUrl: translation.originalDocument || null,
    latestFinalDocument: translation.latestFinalDocument || null,
    finalDocuments: translation.finalDocuments || [],
  });

  const handleOpenSignatureView = () => {
    if (!translation.id || !translation.projectId || !googleDocId) {
      toast.error('This document is not ready to sign yet.');
      return;
    }
    setShowSignatureView(true);
  };

  const handleSaveSignedDocument = async ({ blob, fileName, integrityHash, certificateConfig = null }) => {
    try {
      if (!translation.id || !translation.projectId) {
        throw new Error('Missing translation or project ID for signing.');
      }

      const docSvc = documentService(apiClient);
      let googleDocTitle = null;
      try {
        if (googleDocId && googleAccessToken) {
          const googleDocs = new GoogleDocsService(googleAccessToken);
          const metadata = await googleDocs.getFileMetadata(googleDocId);
          googleDocTitle = metadata?.name || null;
        }
      } catch (metadataError) {
        console.warn('Could not fetch Google Doc title for signed file naming:', metadataError.message);
      }

      const isP12Mode = Boolean(
        certificateConfig?.enabled &&
        !certificateConfig?.usbMode &&
        certificateConfig?.certificateFile
      );

      let data;
      if (isP12Mode) {
        data = await docSvc.uploadTranslationFinalDocumentWithCertificate(
          translation.projectId,
          translation.id,
          blob,
          fileName,
          certificateConfig.certificateFile,
          certificateConfig.certificatePassword || '',
          {
            documentHash: integrityHash,
            signerDisplayName: certificateConfig.signerDisplayName || undefined,
            signerContactInfo: certificateConfig.signerContactInfo || undefined,
            signerTitle: certificateConfig.signerTitle || undefined,
            signatureReason: certificateConfig.signatureReason || undefined,
            signatureLocation: certificateConfig.signatureLocation || undefined,
            certificateAnchor: certificateConfig.certificateAnchor || undefined,
            certificateAppearance: certificateConfig.certificateAppearance || undefined,
            googleDocTitle,
          }
        );
      } else {
        data = await docSvc.uploadTranslationFinalDocument(
          translation.projectId,
          translation.id,
          blob,
          fileName,
          integrityHash,
          googleDocTitle
        );
      }

      const updatedFinalDocument = data.finalDocument;
      setTranslation(prev => ({
        ...prev,
        latestFinalDocument: updatedFinalDocument,
        finalDocuments: [
          updatedFinalDocument,
          ...(prev.finalDocuments || []).filter(doc => doc.id !== updatedFinalDocument.id),
        ],
      }));
      // Signature modal stays open: the refreshed finalDocuments list makes it
      // auto-switch to its built-in "Signed Versions" view for the new version.
      toast.success(t('projectDetails.messages.documentSignedSuccess', 'Signed document saved successfully.'));
    } catch (error) {
      console.error('Error uploading signed document:', error);
      toast.error(error?.response?.data?.error || error.message || t('projectDetails.messages.documentSignedError', 'Failed to upload the signed document. Please try again.'));
    }
  };

  // Show loading while Google auth is initializing OR translation data is loading
  if (loading || isGoogleLoading) {
    return <Spinner />;
  }

  // If using Google Docs mode but not authenticated, show sign-in prompt
  if (useGoogleDocs && !isGoogleAuthenticated) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
        <div style={{ maxWidth: '500px', padding: '40px', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📝</div>
          <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
            {t('landingPage.signInWithGoogle')}
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '24px', lineHeight: '1.6' }}>
            {t('googleDocs.messages.loginRequired')}
          </p>
          <button
            onClick={googleLogin}
            style={{
              padding: '12px 24px',
              backgroundColor: '#4285f4',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>{t('landingPage.signInWithGoogle')}</span>
          </button>
          <div style={{ marginTop: '20px' }}>
            <button
              onClick={() => navigate('/translator/dashboard')}
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: 'none',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              ← {t('projectDetails.back')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If loading document into Google Docs, show loading state
  if (useGoogleDocs && !googleDocId && contentLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner />
          <p style={{ marginTop: '20px', color: '#6b7280', fontSize: '16px' }}>
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  // Render Google Docs Editor if in Google Docs mode and document loaded
  if (useGoogleDocs && googleDocId) {
    return (
      <>
        <FullScreenGoogleDocsViewer
          originalDocument={translation.originalDocument}
          translatedDocumentId={googleDocId}
          documentTitle={translation.documentTitle || `Translation ${translationId}`}
          documentType={translation.documentType}
          translationId={translationId}
          saving={saving}
          readyForApproval={translation.readyForApproval}
          sourceLanguage={translation.sourceLanguage}
          targetLanguage={translation.targetLanguage}
          additionalContext={translation.additionalContext}
          persistedRetranslationComments={translation.retranslationComments}
          latestFinalDocument={translation.latestFinalDocument}
          onSign={handleOpenSignatureView}
          onExport={handleExportGoogleDoc}
          onSave={handleSaveTranslation}
          onApprove={handleApproveTranslation}
        />
        {showSignatureView && (
          <SignatureStampView
            translations={[buildCurrentSigningTranslation()]}
            projectName={translation.documentTitle || `Translation ${translationId}`}
            projectId={translation.projectId}
            documentHolderName={translation.projectName || translation.documentHolder || translation.documentTitle || `Translation ${translationId}`}
            userProfile={userProfile}
            sourceLanguage={translation.sourceLanguage}
            targetLanguage={translation.targetLanguage}
            finalDocuments={translation.finalDocuments || []}
            conformityNumberSeed={translation.documentId || translationId}
            onClose={() => setShowSignatureView(false)}
            onSave={handleSaveSignedDocument}
            onSendToClient={null}
          />
        )}
      </>
    );
  }

  // Fallback: Render TinyMCE Editor if Google Docs fails to load
  return (
    <div className="translation-editor-page">
      <FullScreenTranslationViewer
        originalDocument={translation.originalDocument}
        htmlContent={htmlContent || translation.finalTranslation || translation.aiTranslation}
        onHtmlChange={() => {}} // No-op: auto-save now uses onHtmlUpdate only
        editorRef={editorRef}
        documentType={translation.documentType}
        documentTitle={translation.documentTitle || `Translation ${translationId}`}
        onSave={handleSaveTranslation}
        onApprove={handleApproveTranslation}
        saving={saving}
        lastSaved={lastSaved}
        selectedPage={selectedPage}
        totalPages={htmlFiles.length || 1}
        onPageChange={handlePageChange}
        // Chatbot props
        documentId={translation.documentId || translationId}
        onHtmlUpdate={handleHtmlUpdate}
        selectedText={selectedText}
        selectionContext={selectionContext}
        onProcessMessage={handleChatbotMessage}
        onSelectionChange={handleSelectionChange}
        chatbotConfig={chatbotConfig}
        sourceLanguage={translation.sourceLanguage}
        targetLanguage={translation.targetLanguage}
        // PDF export props - use currentHTML for the current page
        allHtmlPages={htmlFiles.length > 0 ? htmlFiles.map((file, index) => {
          return index === selectedPage - 1 ? htmlContent : file.content;
        }) : [htmlContent]}
        projectName={translation.documentHolder || `Translation_${translationId}`}
      />
    </div>
  );
};

export default TranslationEditor;
